import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigContext } from './utils/Config';
import {
  Box, 
  Grid, 
  GridItem, 
  Image, 
  Text, 
  Spinner, 
  Flex, 
  Alert, 
  AlertIcon, 
  AlertTitle, 
  AlertDescription,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import fetchData from './utils/Util';

const SubscriptionMagazine = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [magazines, setMagazines] = useState([]);
  const [boUserData, setBoUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMagazine, setSelectedMagazine] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Get sale_id for magazines - from backoffice_subscription_magazines_sale_nid variable
  const fetchMagazineSaleId = async () => {
    if (!config.siteData || !config.siteData.backoffice_url) {
      throw new Error('No backoffice URL available');
    }

    const response = await fetchData(
      `${config.siteData.backoffice_url}/api/v1.0/invoke-function-public/variable_get?params[0]=backoffice_subscription_magazines_sale_nid`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch magazine sale ID');
    }
    
    const result = await response.json();
    if (!result[0]) {
      throw new Error('No magazine sale ID returned');
    }
    
    return result[0];
  };

  // Fetch magazines and user data
  useEffect(() => {
    const fetchBoUserData = async () => {
      try {
        // Only fetch user data if user is logged in
        if (config.userData && config.userData.uuid && config.userData.bo_access_token) {
          const response = await fetchData(
            `${config.siteData.backoffice_url}/api/v1.0/datatable-clients?filter[uuid][value]=${config.userData.uuid}&access_token=${config.userData.bo_access_token}`,
            { mockData: 'api-me' }
          );
          
          if (!response.ok) {
            throw new Error(response.title || t('subscriptionMagazine.networkError'));
          }
          
          const result = await response.json();
          const data = result.data[0];
          setBoUserData(data);
        }
        
      } catch (error) {
        setError(error.message);
      }
    };

    const fetchMagazines = async (saleId) => {
      try {
        if (!config.siteData || !config.siteData.backoffice_url) {
          return;
        }

        const response = await fetchData(
          `${config.siteData.backoffice_url}/api/v1.0/catalogue-parts?sale_id=${saleId}&sort=weight&access_token=${config.userData.bo_access_token}`,
          { mockData: 'api-catalogue-parts' }
        );
        
        if (!response.ok) {
          throw new Error(response.title || t('subscriptionMagazine.networkError'));
        }
        
        const result = await response.json();
        
        // Transform API data to our magazine format
        const transformedMagazines = result.data
          .filter(item => item.image && item.image.url)
          .map(item => ({
            id: item.id,
            title: item.title || item.name || `Magazine ${item.id}`,
            coverImage: item.image?.url || "https://placehold.co/300x400/cccccc/ffffff?text=No+Image",
            flipbookUrl: item.pdf_link?.url || "#"
          }));
        
        setMagazines(transformedMagazines);
        
      } catch (error) {
        setError(error.message);
        console.error('Error fetching magazines:', error);
      }
    };

    const fetchDataAsync = async () => {
      try {
        await fetchBoUserData();
        const saleId = await fetchMagazineSaleId();
        await fetchMagazines(saleId);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataAsync().catch((error) => {
      setError(error.message);
      setIsLoading(false);
    });
  }, [config, t]);

  // Check if user is logged in
  const isLoggedIn = () => {
    return config.userData && config.userData.bo_access_token;
  };

  // Check if user has subscription access
  const hasSubscriptionAccess = () => {
    if (!boUserData) {
      return false;
    }

    // Get subscription status using same logic as MyAccount
    const getSubscriptionStatus = () => {
      // If subscription_type is falsy (null, undefined, empty string) or 'none'
      if (!boUserData.subscription_type || boUserData.subscription_type === 'none') {
        // If there's a cancellation date, check if it's in the future or past
        if (boUserData.cancellation_date) {
          const expirationDate = new Date(boUserData.cancellation_date);
          const now = new Date();
          // If cancellation date is in the future, this is an error state
          if (expirationDate > now) {
            return 'error';
          }
          // If cancellation date is in the past, subscription expired
          return 'expired';
        }
        // No cancellation date means never had a subscription
        return 'none';
      }
      
      // Has a subscription type (digital/hardcopy), check if still active
      if (boUserData.cancellation_date) {
        const expirationDate = new Date(boUserData.cancellation_date);
        const now = new Date();
        return expirationDate > now ? 'active' : 'expired';
      }
      
      // Has subscription type but no cancellation date - active
      return 'active';
    };

    const status = getSubscriptionStatus();
    // Only allow access if status is 'active'
    return status === 'active';
  };

  // Handle magazine click
  const handleMagazineClick = (magazine) => {

    // Don't do anything if there's no flipbook URL
    if (!magazine.flipbookUrl || magazine.flipbookUrl === "#") {
      return;
    }

    // Check if user is logged in
    if (!isLoggedIn()) {
      // Redirect to login page
      window.location.href = '/login';
      return;
    }

    // Check if user has subscription access
    if (!hasSubscriptionAccess()) {
      // Show subscription required modal
      setSelectedMagazine(magazine);
      onOpen();
      return;
    }

    // User has access, open flipbook
    window.open(magazine.flipbookUrl, '_blank');
  };

  // Handle subscription purchase redirect
  const handleSubscriptionPurchase = () => {
    // Redirect to subscription purchase page
    window.location.href = '/subscription-purchase';
  };

  if (isLoading || !config.siteData) {
    return (
      <Flex width="100%" height="400px" justifyContent="center" alignItems="center">
        <Spinner color="blue.500" size="xl" thickness="4px" />
      </Flex>
    );
  }

  if (config.success === false) {
    return (
      <Alert status="error" marginBottom="20px">
        <AlertIcon />
        {t('subscriptionMagazine.cannotAccessSiteSettings')}
      </Alert>
    );
  }

  return (
    <Box className="subscription-magazine" p={6} bg="gray.50" minH="100vh">
      <Box maxW="1400px" mx="auto">
        <Text as="h1" fontSize="3xl" fontWeight="bold" mb={2} textAlign="center" color="gray.800">
          {t('subscriptionMagazine.title')}
        </Text>        

        {error && (
          <Alert status="error" marginBottom="20px" borderRadius="md">
            <AlertIcon />
            <AlertTitle>{t('subscriptionMagazine.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoggedIn() && !hasSubscriptionAccess() && boUserData && (() => {
          // Define the same getSubscriptionStatus function here for the alert
          const getSubscriptionStatus = () => {
            if (!boUserData?.subscription_type || boUserData.subscription_type === 'none') {
              if (boUserData.cancellation_date) {
                const expirationDate = new Date(boUserData.cancellation_date);
                const now = new Date();
                if (expirationDate > now) {
                  return 'error';
                }
                return 'expired';
              }
              return 'none';
            }
            
            if (boUserData.cancellation_date) {
              const expirationDate = new Date(boUserData.cancellation_date);
              const now = new Date();
              return expirationDate > now ? 'active' : 'expired';
            }
            
            return 'active';
          };

          const status = getSubscriptionStatus();
          
          return (
            <Alert 
              status={status === 'error' ? "error" : status === 'expired' ? "warning" : "info"} 
              marginBottom="20px" 
              borderRadius="md" 
              width="fit-content"
            >
              <AlertIcon />
              <AlertDescription>
                {status === 'error' ? (
                  <>
                    {t('subscriptionMagazine.subscriptionError')}{' '}
                    <Button 
                      as="a" 
                      href="/contact" 
                      colorScheme="red" 
                      size="sm" 
                      ml={2}
                    >
                      {t('subscriptionMagazine.contactOffice')}
                    </Button>
                  </>
                ) : status === 'expired' ? (
                  <>
                    {t('subscriptionMagazine.subscriptionExpired')}{' '}
                    <Button 
                      as="a" 
                      href="/subscription-purchase" 
                      colorScheme="orange" 
                      size="sm" 
                      ml={2}
                    >
                      {t('subscriptionMagazine.renewSubscription')}
                    </Button>
                  </>
                ) : (
                  <>
                    {t('subscriptionMagazine.subscriptionRequired')}{' '}
                    <Button 
                      as="a" 
                      href="/subscription-purchase" 
                      colorScheme="blue" 
                      size="sm" 
                      ml={2}
                    >
                      {t('subscriptionMagazine.learnAboutPlans')}
                    </Button>
                  </>
                )}
              </AlertDescription>
            </Alert>
          );
        })()}

        {!isLoggedIn() && (
          <Alert status="info" marginBottom="20px" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              {t('subscriptionMagazine.loginToAccess')}
            </AlertDescription>
          </Alert>
        )}

        <Grid 
          templateColumns={{
            base: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)", 
            md: "repeat(4, 1fr)",
            lg: "repeat(5, 1fr)"
          }}
          gap={6}
          px={4}
        >
          {magazines.map((magazine) => (
            <GridItem key={magazine.id}>
              <Box
                cursor={magazine.flipbookUrl && magazine.flipbookUrl !== "#" ? "pointer" : "default"}
                transition="transform 0.2s, box-shadow 0.2s"
                _hover={magazine.flipbookUrl && magazine.flipbookUrl !== "#" ? {
                  transform: "translateY(-4px)",
                  shadow: "lg"
                } : {}}
                onClick={() => handleMagazineClick(magazine)}
              >
                {/* Magazine Cover */}
                <Box
                  borderRadius="md"
                  overflow="hidden"
                  shadow="md"
                  bg="white"
                  mb={3}
                >
                  <Image
                    src={magazine.coverImage}
                    alt={magazine.title}
                    width="100%"
                    height="auto"
                    aspectRatio="3/4"
                    objectFit="cover"
                    fallbackSrc="https://placehold.co/300x400/cccccc/ffffff?text=Loading..."
                  />
                </Box>
                
                {/* Magazine Info */}
                <Box textAlign="center">
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color="gray.800"
                    lineHeight="1.2"
                  >
                    {magazine.title}
                  </Text>
                </Box>
              </Box>
            </GridItem>
          ))}
        </Grid>
      </Box>

      {/* Subscription Required Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('subscriptionMagazine.subscriptionRequiredTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              {t('subscriptionMagazine.subscriptionRequiredMessage', { 
                magazineTitle: selectedMagazine?.title 
              })}
            </Text>
            <Text color="gray.600">
              {t('subscriptionMagazine.subscriptionBenefits')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={onClose}>
              {t('subscriptionMagazine.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={handleSubscriptionPurchase}>
              {t('subscriptionMagazine.purchaseSubscription')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SubscriptionMagazine;